import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { FiChevronLeft, FiChevronRight } from 'react-icons/fi';
import { SliderItem, Category } from '../../types';
import { getImageUrl } from '../../config';
import { categoryService } from '../../services/categoryService';
import { useCategoryFilter } from '../../context/CategoryFilterContext';

// Interfaces
interface ProductCardProps {
  isFirst?: boolean;
}

interface SliderProps {
  slides: SliderItem[];
  autoPlay?: boolean;
  autoPlayInterval?: number;
}

// Convertir los iconos a componentes React válidos
const ChevronLeft = FiChevronLeft as React.ComponentType;
const ChevronRight = FiChevronRight as React.ComponentType;

// Styled Components para el Slider Principal
const SliderContainer = styled.div`
  position: relative;
  width: 100%;
  max-width: 1366px;
  height: auto; /* Dejar que el aspect-ratio mande */
  aspect-ratio: 1366 / 420; /* Proporción exacta de tu imagen */
  margin: 0 auto;
  overflow: hidden;
  border-radius: 24px;
  box-shadow: 0 10px 30px rgba(0,0,0,0.15);

  @media (max-width: 1366px) {
    width: 100%;
    border-radius: 16px;
  }

  @media (max-width: 768px) {
    width: 100%;
    height: auto;
    aspect-ratio: auto !important; /* Ignorar la proporción de escritorio */
    min-height: 200px;
    border-radius: 12px;
  }
`;

const SliderTrack = styled.div.withConfig({
  shouldForwardProp: (prop) => !['currentSlide', 'isTransitioning'].includes(prop)
})<{ currentSlide: number; isTransitioning: boolean }>`
  display: flex;
  transition: ${props => props.isTransitioning ? 'transform 0.5s ease-in-out' : 'none'};
  transform: translateX(-${props => props.currentSlide * 100}%);
  height: 100%;
`;

const SlideContainer = styled.div`
  position: relative;
  min-width: 100%;
  height: 100%;
  background-position: center;
  background-size: cover;
  background-repeat: no-repeat;
  display: flex;
  justify-content: center;
  align-items: stretch;
  padding: 0;

  @media (max-width: 768px) {
    background-position: center;
    background-size: cover;
    height: auto;
  }
`;

const SlideImage = styled.img`
  width: 100%;
  height: auto;
  display: block;
  object-fit: cover !important;
  object-position: center;
  z-index: 0;
  
  @media (min-width: 769px) {
    position: absolute;
    top: 0;
    left: 0;
    height: 100%;
  }
`;

const ProductGrid = styled.div`
  position: absolute;
  top: 0;
  right: 0;
  display: flex;
  width: 100%;
  height: 100%;
  justify-content: flex-end;
  z-index: 1;
  pointer-events: none;

  @media (max-width: 1366px) {
    justify-content: center;
  }

  @media (max-width: 768px) {
    justify-content: center;
    width: 100%;
  }
`;

const HoverText = styled.span`
  display: none;
  position: absolute;
  top: 80%;
  left: 50%;
  transform: translate(-50%, -50%);
  background: white;
  padding: 8px 16px;
  border-radius: 4px;
  color: #FDBF37;
  font-weight: bold;
  white-space: nowrap;
  
  &::before {
    content: '→';
    margin-right: 8px;
  }
`;

const ProductCard = styled.div.withConfig({
  shouldForwardProp: (prop) => !['isFirst'].includes(prop)
})<ProductCardProps>`
  position: relative;
  background: transparent;  
  padding: 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  transition: all 0.3s ease;
  overflow: hidden;
  width: 19%;
  height: 100%;
  margin: 0;
  cursor: pointer;
  pointer-events: auto; /* Reactivar clicks para los productos */
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: rgba(0, 0, 0, 0);
    transition: background 0.3s ease;
    z-index: 1;
    border-radius: 6px;
    display: ${props => props.isFirst ? 'none' : 'block'};
  }
  
  &:hover {
    &::before {
      background: ${props => props.isFirst ? 'transparent' : 'rgba(0, 0, 0, 0.12)'};
    }
    
    ${HoverText} {
      display: block;
      z-index: 2;
    }
  }

  @media (max-width: 1366px) {
    width: 20%;
  }

  @media (max-width: 768px) {
    width: 40%;
    margin: 0 1%;
  }
`;

const NavigationButton = styled.button<{ direction: 'left' | 'right' }>`
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
  ${props => props.direction === 'left' ? 'left: 0px;' : 'right: 0px;'}
  background: rgba(0, 0, 0, 0.5);
  color: white;
  border: none;
  border-radius: 50%;
  width: 40px;
  height: 40px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: background 0.3s ease;
  
  &:hover {
    background: rgba(0, 0, 0, 0.8);
  }
  
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

// Styled Components para el Carrusel de Categorías INFINITO REAL
const StyledCategoryCarousel = styled.div`
  position: relative;
  width: 100%;
  padding: 20px 30px;
  margin-top: 20px;
  overflow: hidden;
  user-select: none; // Evitar selección de texto durante navegación
  
  @media (max-width: 768px) {
    padding: 15px 20px;
  }
`;

const StyledCategoryTrack = styled.div<{ transform: number }>`
  position: relative;
  height: 120px;
  width: 100%;
  transform: translateX(-${props => props.transform}px);
  will-change: transform;
  transition: transform 0.3s ease;
`;

const StyledCategoryItem = styled.div`
  flex: 0 0 auto;
  width: 100px;
  display: flex;
  flex-direction: column;
  align-items: center;
  cursor: pointer;
  transition: transform 0.3s ease;

  &:hover {
    transform: scale(1.05);
  }
`;

const StyledCategoryImage = styled.img`
  width: 100px;
  height: 100px;
  object-fit: contain;
  margin-bottom: 8px;
`;

const StyledCategoryName = styled.span`
  font-size: 12px;
  color: #333;
  text-align: center;
  line-height: 1.2;
  max-width: 90px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const CategoryNavigationButton = styled(NavigationButton)`
  top: 50%;
  transform: translateY(-90%);
  width: 36px;
  height: 36px;
  background: rgba(36, 42, 91, 0.8);
  color: white;
  z-index: 2;
  transition: all 0.3s ease;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.2);

  
  
  &:active {
    background: rgba(36, 42, 91, 1);
    transform: translateY(-50%) scale(0.95);
  }
  
  svg {
    width: 16px;
    height: 16px;
  }
`;

// Componente Principal
const Slider: React.FC<SliderProps> = ({ slides, autoPlay = true, autoPlayInterval = 7000 }) => {
  const navigate = useNavigate();
  const { setSelectedCategoryFilter } = useCategoryFilter();
  const [currentSlide, setCurrentSlide] = useState(1); // Empezar en el primer slide real (índice 1)
  const [isTransitioning, setIsTransitioning] = useState(true);
  const [isAutoPlaying, setIsAutoPlaying] = useState(true);
  const [categoryScroll, setCategoryScroll] = useState(0);
  const [categories, setCategories] = useState<Category[]>([]);
  const [isMobile, setIsMobile] = useState(false);
  const [isNavigating, setIsNavigating] = useState(false); // Nuevo estado para debounce
  const categoryTrackRef = useRef<HTMLDivElement>(null);
  const autoPlayRef = useRef<NodeJS.Timeout | null>(null);

  // Crear slides duplicados para el bucle infinito
  const extendedSlides = useMemo(() => {
    if (slides.length === 0) return [];
    
    // Agregar el último slide al principio y el primer slide al final
    const lastSlide = slides[slides.length - 1];
    const firstSlide = slides[0];
    
    return [lastSlide, ...slides, firstSlide];
  }, [slides]);

  const totalSlides = extendedSlides.length;

  // Detectar tamaño de pantalla
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth <= 768);
    };
    
    checkMobile();
    window.addEventListener('resize', checkMobile);
    
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  // Cargar categorías
  useEffect(() => {
    const fetchCategories = async () => {
      try {
        const fetchedCategories = await categoryService.getCategories();
        setCategories(fetchedCategories);
      } catch (error) {
        console.error('Error fetching categories:', error);
      }
    };
    fetchCategories();
  }, []);

  // Funciones de navegación manual (pausan el auto-play temporalmente)
  const handleNextSlide = useCallback(() => {
    if (totalSlides <= 1 || isTransitioning || isNavigating) return;
    
    setIsNavigating(true); // Activar debounce
    
    // Pausar auto-play temporalmente
    setIsAutoPlaying(false);
    if (autoPlayRef.current) {
      clearInterval(autoPlayRef.current);
      autoPlayRef.current = null;
    }
    
    setIsTransitioning(true);
    setCurrentSlide(prev => prev + 1);
    
    // Desactivar debounce después de la transición
    setTimeout(() => {
      setIsNavigating(false);
    }, 600);
    
    // Reanudar auto-play después de 3 segundos
    setTimeout(() => {
      setIsAutoPlaying(true);
    }, 3000);
  }, [totalSlides, isTransitioning, isNavigating]);

  const handlePrevSlide = useCallback(() => {
    if (totalSlides <= 1 || isTransitioning || isNavigating) return;
    
    setIsNavigating(true); // Activar debounce
    
    // Pausar auto-play temporalmente
    setIsAutoPlaying(false);
    if (autoPlayRef.current) {
      clearInterval(autoPlayRef.current);
      autoPlayRef.current = null;
    }
    
    setIsTransitioning(true);
    setCurrentSlide(prev => prev - 1);
    
    // Desactivar debounce después de la transición
    setTimeout(() => {
      setIsNavigating(false);
    }, 600);
    
    // Reanudar auto-play después de 3 segundos
    setTimeout(() => {
      setIsAutoPlaying(true);
    }, 3000);
  }, [totalSlides, isTransitioning, isNavigating]);

  // Función para auto-play (solo para uso interno)
  const nextSlideAuto = useCallback(() => {
    if (totalSlides <= 1 || isTransitioning || isNavigating) return;
    
    setIsTransitioning(true);
    setCurrentSlide(prev => prev + 1);
  }, [totalSlides, isTransitioning, isNavigating]);

  // Auto-play del slider principal
  useEffect(() => {
    if (isAutoPlaying && totalSlides > 1 && !isTransitioning && !isNavigating) {
      autoPlayRef.current = setInterval(() => {
        nextSlideAuto();
      }, 5000);
      
      return () => {
        if (autoPlayRef.current) {
          clearInterval(autoPlayRef.current);
          autoPlayRef.current = null;
        }
      };
    }
  }, [isAutoPlaying, totalSlides, nextSlideAuto, isTransitioning, isNavigating]);

  // Manejar el bucle infinito - simplificado
  useEffect(() => {
    if (!isTransitioning) return;

    const timer = setTimeout(() => {
      if (currentSlide === 0) {
        // Saltar al último slide real
        setCurrentSlide(slides.length);
        setIsTransitioning(false);
      } else if (currentSlide === totalSlides - 1) {
        // Saltar al primer slide real
        setCurrentSlide(1);
        setIsTransitioning(false);
      } else {
        // Terminar la transición normalmente
        setIsTransitioning(false);
      }
    }, 500); // Reducir el tiempo de transición

    return () => clearTimeout(timer);
  }, [currentSlide, isTransitioning, slides.length, totalSlides]);

  // Función para scroll continuo sin límites
  const handleCategoryScroll = (direction: 'left' | 'right') => {
    if (categories.length === 0) return;
    
    const itemWidth = 110; // Reducido para mostrar 11 categorías (100px + 10px gap)
    const scrollAmount = itemWidth * 1; // Scroll de 1 item por vez
    
    setCategoryScroll(prev => {
      const newPosition = direction === 'right' ? prev + scrollAmount : prev - scrollAmount;
      return newPosition;
    });
  };

  // Limpiar intervalos al desmontar
  useEffect(() => {
    return () => {
      if (autoPlayRef.current) {
        clearInterval(autoPlayRef.current);
        autoPlayRef.current = null;
      }
    };
  }, []);

  // Manejar click en categoría
  const handleCategoryClick = (category: Category) => {
    navigate(`/productos?categoria=${category.name}`);
  };

  // Renderizar carrusel con bucle dinámico continuo
  const renderCategories = () => {
    if (categories.length === 0) return null;
    
    const itemWidth = 110; // Mismo valor que en handleCategoryScroll
    const containerWidth = 1200; // Ancho del contenedor visible
    const buffer = 12; // Buffer más amplio para mayor estabilidad
    const totalItemsToRender = Math.ceil(containerWidth / itemWidth) + buffer;
    
    // Calcular el índice de inicio basado en la posición actual del scroll
    const startPosition = categoryScroll / itemWidth;
    const startIndex = Math.floor(startPosition) - Math.floor(buffer / 2); // Centrar el buffer
    
    const renderedCategories: React.ReactElement[] = [];
    
    // Generar las categorías necesarias para llenar el área visible + buffer
    for (let i = 0; i < totalItemsToRender; i++) {
      const currentIndex = startIndex + i;
      
      // Mejorar el cálculo del módulo para manejar correctamente números negativos
      const categoryIndex = ((currentIndex % categories.length) + categories.length) % categories.length;
      const category = categories[categoryIndex];
      
      if (!category) continue; // Saltar si no hay categoría (seguridad extra)
      
      // Posición absoluta de este item
      const itemPosition = currentIndex * itemWidth;
      
      // Crear una clave única más estable
      const itemKey = `cat-${categoryIndex}-pos-${Math.floor(currentIndex / categories.length)}`;
      
      renderedCategories.push(
        <StyledCategoryItem 
          key={itemKey}
          onClick={() => handleCategoryClick(category)}
          style={{
            position: 'absolute',
            left: `${itemPosition}px`,
            width: '100px',
            opacity: 1,
            transform: 'translateZ(0)', // Activar aceleración de hardware
            transition: 'opacity 0.2s ease',
            willChange: 'transform' // Optimización para animaciones
          }}
        >
          <StyledCategoryImage
            src={category.image_url ? getImageUrl(category.image_url) : '/placeholder-image.jpg'}
            alt={category.name}
            loading="lazy"
            onError={(e) => {
              if (e && e.target) {
                const target = e.target as HTMLImageElement;
                target.src = '/placeholder-image.jpg';
              }
            }}
          />
          <StyledCategoryName>{category.name}</StyledCategoryName>
        </StyledCategoryItem>
      );
    }
    
    return renderedCategories;
  };

  // Inicializar scroll en posición centrada para permitir navegación en ambas direcciones
  useEffect(() => {
    if (categories.length > 0) {
      const itemWidth = 110; // Mismo valor que en las otras funciones
      // Comenzar en una posición que permita navegación hacia la izquierda desde el inicio
      // Esto representa como si ya hubiéramos navegado varias "vueltas" del carrusel
      const bufferOffset = itemWidth * categories.length * 10; // Múltiples ciclos de offset
      setCategoryScroll(bufferOffset);
    }
  }, [categories]);

  // Manejar click en slide
  const handleSlideClick = (slide: SliderItem, containerIndex: number) => {
    console.log('🔍 Slider click detected:', {
      containerIndex,
      slide_id: slide.id,
      category_id: slide.category_id,
      categories: categories.length
    });
    
    // Contenedor 1 (índice 0) - Redirigir a categoría
    if (containerIndex === 0) {
      console.log('📍 Container 1 clicked - checking category_id:', slide.category_id);
      
      if (slide.category_id) {
        const category = categories.find(cat => cat.id === slide.category_id);
        console.log('🏷️ Category found:', category);
        
        if (category) {
          console.log('✅ Navigating to category:', category.name);
          // Establecer el filtro de categoría en el contexto
          setSelectedCategoryFilter(category.name);
          navigate(`/productos?categoria=${category.name}`);
        } else {
          console.error('❌ Category not found for ID:', slide.category_id);
          console.log('📋 Available categories:', categories.map(cat => ({ id: cat.id, name: cat.name })));
          // Intentar recargar las categorías
          categoryService.getCategories().then(freshCategories => {
            const freshCategory = freshCategories.find(cat => cat.id === slide.category_id);
            if (freshCategory) {
              console.log('✅ Category found after refresh:', freshCategory.name);
              setSelectedCategoryFilter(freshCategory.name);
              navigate(`/productos?categoria=${freshCategory.name}`);
            } else {
              console.error('❌ Category still not found after refresh. Available IDs:', freshCategories.map(cat => cat.id));
            }
          }).catch(error => {
            console.error('❌ Error refreshing categories:', error);
          });
        }
      } else {
        console.warn('⚠️ No category_id found for slide:', slide.id);
      }
      return;
    }
    
    if (!slide.container_products) {
      console.warn('container_products no está definido para este slide');
      return;
    }

    const containerKey = containerIndex === 4 ? 'container5' : `container${containerIndex + 1}` as keyof typeof slide.container_products;
    const productId = slide.container_products[containerKey];
    
    if (productId) {
      navigate(`/producto/${productId}`);
    } else if (slide.link_url) {
      window.location.href = slide.link_url;
    }
  };

  return (
    <>
      <SliderContainer>
        <SliderTrack currentSlide={currentSlide} isTransitioning={isTransitioning}>
          {extendedSlides.map((slide, index) => (
            <SlideContainer
              key={`${slide.id}-${index}`}
              onClick={() => {
                // Si es un click directo al slide (no a un producto)
                if (slide.link_url) {
                  window.location.href = slide.link_url;
                }
              }}
            >
              <picture>
                {slide.mobile_image_url && (
                  <source 
                    media="(max-width: 768px)" 
                    srcSet={getImageUrl(slide.mobile_image_url)} 
                  />
                )}
                <SlideImage 
                  src={getImageUrl(slide.image_url)} 
                  alt={slide.title} 
                />
              </picture>
              <ProductGrid>
                {isMobile 
                  ? [3, 4, 5].map((num, idx) => (
                      <ProductCard 
                        key={num} 
                        isFirst={false}
                        onClick={() => handleSlideClick(slide, num - 1)}
                      >
                        <HoverText>Ver producto</HoverText>
                      </ProductCard>
                    ))
                  : [1, 2, 3, 4, 5].map((num, idx) => (
                      <ProductCard 
                        key={num} 
                        isFirst={idx === 0}
                        onClick={() => handleSlideClick(slide, idx)}
                      >
                        <HoverText>{idx === 0 ? 'Ver categoría' : 'Ver producto'}</HoverText>
                      </ProductCard>
                    ))
                }
              </ProductGrid>
            </SlideContainer>
          ))}
        </SliderTrack>
        <NavigationButton direction="left" onClick={handlePrevSlide}>
          <ChevronLeft />
        </NavigationButton>
        <NavigationButton direction="right" onClick={handleNextSlide}>
          <ChevronRight />
        </NavigationButton>
      </SliderContainer>

      <StyledCategoryCarousel>
        <CategoryNavigationButton 
          direction="left" 
          onClick={() => handleCategoryScroll('left')}
          style={{
            display: categories.length > 1 ? 'flex' : 'none' // Mostrar si hay más de 1 categoría
          }}
        >
          <ChevronLeft />
        </CategoryNavigationButton>

        <StyledCategoryTrack 
          ref={categoryTrackRef} 
          transform={categoryScroll}
          style={{
            transition: 'transform 0.3s ease',
            minHeight: '120px' // Asegurar altura mínima para evitar colapso
          }}
        >
          {renderCategories()}
        </StyledCategoryTrack>

        <CategoryNavigationButton 
          direction="right" 
          onClick={() => handleCategoryScroll('right')}
          style={{
            display: categories.length > 1 ? 'flex' : 'none' // Mostrar si hay más de 1 categoría
          }}
        >
          <ChevronRight />
        </CategoryNavigationButton>
      </StyledCategoryCarousel>
    </>
  );
};

export default Slider;
